import { useState, useMemo, useEffect } from 'react';
import { useLang } from '../../config/i18n';
import { TODAY } from '../../config/constants';
import { daysBetween, parseDate, fmtDate, getPaymentSlots, getTenantStatus, getCurrentSlot } from '../../lib/helpers';
import { Modal, Field, Badge, StatCard, AlertBar, SaveBtn, actBtnStyle } from '../shared/UI';

export default function DashboardTab({ tenants, setTenants, units, contracts, invoices = [], monthly, setMonthly }){
  const {S, lang, dir, font} = useLang();
  const T = S.dash;
  const [filter, setFilter]         = useState("all");
  const [distFilter, setDistFilter] = useState("all");
  const [search, setSearch]         = useState("");
  const [showAdd, setShowAdd]       = useState(false);
  const [addForm, setAddForm]       = useState({});
  const [showDetail, setShowDetail] = useState(null);
  const [showEdit, setShowEdit]     = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [showExport, setShowExport] = useState(false);

  // Mobile detection — switches the units list from a wide table to stacked cards on phones.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mql.matches);
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  // Contracts = canonical list of units. Invoices = source of truth for occupancy and paid status.
  // Each row in the dashboard is one contract, enriched with its active invoice (if any).
  const allTenants = useMemo(()=>{
    const todayStr = TODAY.toISOString().slice(0,10);
    const contractDistrict = c => c.district ||
      (c.unitName?.includes(' - ') ? c.unitName.split(' - ').slice(1).join(' - ').trim() : '');

    // For a given contract, find its currently-active invoice (today between start and effective end).
    // Match by contractId (most precise) → ejarNum → unit + district (legacy invoices).
    function activeInvoiceFor(c){
      const cu = String(c.unitNumber||c.unitName||'').toLowerCase();
      const cd = String(contractDistrict(c)||'').toLowerCase();
      const candidates = (invoices||[]).filter(inv => {
        if (inv.contractId && c.id && inv.contractId === c.id) return true;
        if (inv.ejarNum && c.ejarNum && String(inv.ejarNum).trim() === String(c.ejarNum).trim()) return true;
        const iu = String(inv.unit||'').toLowerCase();
        const id = String(inv.district||'').toLowerCase();
        return iu === cu && (!id || !cd || id === cd);
      });
      // Pick the one whose period covers today (vacatedAt early-terminates the rental)
      const current = candidates.filter(inv => {
        const effectiveEnd = inv.vacatedAt || inv.contractEnd || '';
        return (inv.contractStart||'') <= todayStr && effectiveEnd >= todayStr;
      });
      if (current.length) return current.sort((a,b)=>(b.contractStart||'').localeCompare(a.contractStart||''))[0];
      return null;
    }

    const curMonthKey = todayStr.slice(0,7);
    // Look up any tenant currently active in Monthly Ops for this contract this month.
    // Covers rentals entered via the new multi-tenant modal that aren't backed by an invoice yet.
    function currentMonthlyTenantFor(c){
      const op = (monthly||{})[`${c.id}-${curMonthKey}`];
      if (!op) return null;
      const list = Array.isArray(op.tenants) ? op.tenants : [];
      // Pick the tenant whose from/to covers today, else the latest one with a from date.
      const covering = list.find(t => t.from && t.to && t.from <= todayStr && t.to >= todayStr);
      if (covering) return covering;
      const anyActive = list.filter(t => t.name && (t.amount||0) > 0).sort((a,b) => (b.from||'').localeCompare(a.from||''));
      return anyActive[0] || null;
    }

    // Dashboard shows only rentable units — archived contracts and storage warehouses don't belong here.
    return (contracts||[]).filter(c => c.status !== 'archived' && c.type !== 'storage').map(c => {
      const inv = activeInvoiceFor(c);
      const district = contractDistrict(c);
      // Fallback source: Monthly Ops tenant for current month (no invoice created yet).
      const monthlyTenant = inv ? null : currentMonthlyTenantFor(c);
      if (!inv && !monthlyTenant) {
        // No active rental anywhere → unit is vacant
        return {
          id: c.id,
          unit: c.unitNumber || c.unitName || '',
          district,
          ejarNum: c.ejarNum || '',
          name: '', phone: '', contractStart: '', contractEnd: '',
          rent: 0, available: true, payments: {}, paidAt: null,
        };
      }
      if (!inv && monthlyTenant){
        // Occupied via Monthly Ops only. Build a synthetic row.
        return {
          id: c.id,
          unit: c.unitNumber || c.unitName || '',
          district,
          ejarNum: c.ejarNum || '',
          name: monthlyTenant.name || '',
          phone: '',
          contractStart: monthlyTenant.from || '',
          contractEnd:   monthlyTenant.to   || '',
          rent: Number(monthlyTenant.amount) || 0,
          available: false,
          paidAt: monthlyTenant.from || null,
          payments: monthlyTenant.from ? { [monthlyTenant.from.slice(0,7)]: { paid: true, date: monthlyTenant.from } } : {},
          source: 'monthly',
        };
      }
      // Active invoice → unit is rented. Mirror paid status from the invoice's paidAt.
      // For getTenantStatus compatibility, materialise a synthetic month-payment entry from paidAt.
      const monthKey = (inv.paidAt || todayStr).slice(0,7);
      const payments = inv.paidAt ? { [monthKey]: { paid: true, date: inv.paidAt } } : {};
      return {
        id: c.id,
        unit: inv.unit || c.unitNumber || c.unitName || '',
        district: inv.district || district,
        ejarNum: c.ejarNum || inv.ejarNum || '',
        name: inv.customerName || '',
        phone: inv.customerPhone || '',
        contractStart: inv.contractStart || '',
        contractEnd: inv.contractEnd || '',
        vacatedAt: inv.vacatedAt || null,
        rent: Number(inv.rent) || 0,
        available: false,
        paidAt: inv.paidAt || null,
        invoiceId: inv.id,
        invoiceNo: inv.invoiceNo,
        payments,
      };
    });
  }, [contracts, invoices, monthly]);

  const DISTRICTS = [...new Set(allTenants.map(t=>t.district).filter(Boolean))];

  const enriched = useMemo(()=>allTenants.map(t=>({
    ...t,
    status:getTenantStatus(t),
    daysToExpiry:t.contractEnd?daysBetween(TODAY,parseDate(t.contractEnd)):null,
    currentSlot:getCurrentSlot(t),
    slots:t.contractStart&&t.contractEnd?getPaymentSlots(t.contractStart,t.contractEnd):[],
  })),[allTenants]);

  const filtered = useMemo(()=>enriched.filter(t=>{
    if(filter!=="all"&&t.status!==filter) return false;
    if(distFilter!=="all"&&t.district!==distFilter) return false;
    if(search&&!t.name.includes(search)&&!t.unit.toLowerCase().includes(search.toLowerCase())&&!t.district.includes(search)) return false;
    return true;
  }),[enriched,filter,distFilter,search]);

  const stats = useMemo(()=>({
    occupied:enriched.filter(t=>!t.available&&t.status!=="expired").length,
    available:enriched.filter(t=>t.available).length,
    overdue:enriched.filter(t=>t.status==="overdue").length,
    dueSoon:enriched.filter(t=>["due_today","due_soon"].includes(t.status)).length,
    expiring:enriched.filter(t=>!t.available&&t.daysToExpiry!==null&&t.daysToExpiry>=0&&t.daysToExpiry<=7).length,
  }),[enriched]);

  function markPaid(id, key){
    const tenant = tenants.find(t=>t.id===id);
    setTenants(p=>p.map(t=>t.id===id?{...t,payments:{...t.payments,[key]:{paid:true,date:TODAY.toISOString().slice(0,10)}}}:t));
    // Cash basis: record revenue in the month the payment is RECEIVED (today)
    // Use contract.id as stable key (contracts are source of truth, matches Monthly tab rows)
    if(tenant && setMonthly){
      const receivedMonthKey = `${TODAY.getFullYear()}-${String(TODAY.getMonth()+1).padStart(2,'0')}`;
      // Prefer Ejar (government-unique) for contract lookup; fall back to unit+district matching for legacy tenants.
      const matchContract = (tenant.ejarNum && (contracts||[]).find(c => String(c.ejarNum||'').trim() === String(tenant.ejarNum||'').trim())) ||
        (contracts||[]).find(c =>
          (c.unitNumber === tenant.unit || c.unitName === tenant.unit) &&
          (!tenant.district || c.district === tenant.district)
        );
      const monthlyUnitId = matchContract ? matchContract.id : `${tenant.unit}-${tenant.district}`;
      const flatKey = `${monthlyUnitId}-${receivedMonthKey}`;
      setMonthly(p=>({...p,[flatKey]:{...(p[flatKey]||{}),
        status:'مشغول',
        tenantName: tenant.name||'',
        rentCollected: (p[flatKey]?.rentCollected||0) + (Number(tenant.rent)||0)
      }}));
    }
    setShowDetail(null);
  }

  function saveTenant(){
    const f=addForm;
    const nextId = Math.max(0,...tenants.map(t=>Number(t.id)||0))+1;
    // Resolve ejarNum: prefer form (passed from ghost contract), fallback to contract lookup by unit+district
    const resolvedEjar = f.ejarNum ||
      ((contracts||[]).find(c =>
        String(c.unitNumber||c.unitName||'').toLowerCase() === String(f.unit||'').toLowerCase() &&
        (!f.district || String(c.district||'').toLowerCase() === String(f.district||'').toLowerCase())
      )?.ejarNum || "");
    if(f._targetId){
      const realTenant = tenants.find(t=>t.id===f._targetId);
      if(realTenant){
        // Update existing tenant record — also backfill ejarNum so future matches go through Ejar
        setTenants(p=>p.map(t=>t.id===f._targetId?{...t,available:false,ejarNum:t.ejarNum||resolvedEjar,name:f.name||"",phone:f.phone||"",contractStart:f.contractStart,contractEnd:f.contractEnd,rent:Number(f.rent)||0,payments:{}}:t));
      } else {
        // Ghost unit (from contracts) — create a new tenant record carrying the contract's Ejar
        setTenants(p=>[...p,{id:nextId,unit:f.unit,district:f.district,ejarNum:resolvedEjar,name:f.name||"",phone:f.phone||"",contractStart:f.contractStart,contractEnd:f.contractEnd,rent:Number(f.rent)||0,available:false,payments:{}}]);
      }
    } else {
      setTenants(p=>[...p,{id:nextId,unit:f.unit,district:f.district,ejarNum:resolvedEjar,name:f.name||"",phone:f.phone||"",contractStart:f.contractStart,contractEnd:f.contractEnd,rent:Number(f.rent)||0,available:false,payments:{}}]);
    }
    setShowAdd(false); setAddForm({});
  }

  function saveEdit(){ setTenants(p=>p.map(t=>t.id===editForm.id?{...t,unit:editForm.unit,district:editForm.district,name:editForm.name,phone:editForm.phone,contractStart:editForm.contractStart,contractEnd:editForm.contractEnd,rent:Number(editForm.rent)||0}:t)); setShowEdit(null); setEditForm({}); }
  function markAvail(id){ setTenants(p=>p.map(t=>t.id===id?{...t,available:true,name:"",phone:"",contractStart:"",contractEnd:"",rent:0,payments:{}}:t)); setShowEdit(null); }

  function openAdd(t){ setAddForm(t?{_targetId:t.id,unit:t.unit,district:t.district,ejarNum:t.ejarNum||"",name:"",phone:"",contractStart:"",contractEnd:"",rent:""}:{unit:"",district:"",ejarNum:"",name:"",phone:"",contractStart:"",contractEnd:"",rent:""}); setShowAdd(true); }
  function openEdit(t){ setEditForm({id:t.id,unit:t.unit,district:t.district,name:t.name,phone:t.phone,contractStart:t.contractStart||"",contractEnd:t.contractEnd||"",rent:String(t.rent||"")}); setShowEdit(t.id); }

  // Mobile-only: tap an occupied row to open its detail modal (read-only status view).
  // Vacant rows are no-op — renting happens in the Invoices/Registration tab, not from the dashboard.
  function handleRowTap(t){
    if(typeof window === 'undefined') return;
    if(!window.matchMedia('(max-width: 768px)').matches) return;
    if(!t.available) setShowDetail(t.id);
  }

  const detailT=enriched.find(t=>t.id===showDetail);

  function exportReport(){
    const sec=(title,list)=>list.length?`${title}\n${"─".repeat(36)}\n${list.map(t=>`  وحدة ${t.unit} | ${t.name||"—"} | ${t.district}${t.rent?` | ${t.rent.toLocaleString()} ريال`:""}`).join("\n")}\n\n`:"";
    return["FindRE — تقرير العمليات",`التاريخ: ${TODAY.toISOString().slice(0,10)}`,"═".repeat(36),"",
      sec("🔴 متأخرون عن الدفع",enriched.filter(t=>t.status==="overdue")),
      sec("🟡 مستحق قريباً",enriched.filter(t=>["due_today","due_soon"].includes(t.status))),
      sec("⏳ عقود تنتهي هذا الأسبوع",enriched.filter(t=>!t.available&&t.daysToExpiry!==null&&t.daysToExpiry>=0&&t.daysToExpiry<=7)),
      sec("✅ مدفوع / منتظم",enriched.filter(t=>t.status==="paid")),
      sec("🔵 وحدات شاغرة",enriched.filter(t=>t.status==="available")),
      sec("⚫ منتهي العقد",enriched.filter(t=>t.status==="expired")),
    ].join("\n");
  }

  const fBtn=(val,lbl)=>(
    <button onClick={()=>setFilter(val)} style={{padding:"6px 15px",borderRadius:20,border:"1px solid",cursor:"pointer",fontSize:12,fontWeight:700,
      background:filter===val?"rgba(200,160,60,0.18)":"rgba(255,255,255,0.04)",color:filter===val?"#c8a03c":"#64748b",
      borderColor:filter===val?"rgba(200,160,60,0.4)":"transparent"}}>{lbl}</button>
  );

  return(
    <div style={{padding:"24px 28px",maxWidth:1320,margin:"0 auto"}}>
      {/* Stats */}
      <div style={{display:"flex",gap:12,marginBottom:22,flexWrap:"wrap"}}>
        <StatCard label="إجمالي الوحدات"  value={(contracts||[]).filter(c=>c.status!=='archived' && c.type!=='storage').length} accent="#c8a03c" icon="🏢"/>
        <StatCard label={T.occupied}      value={stats.occupied}  accent="#e2e8f0" icon="🏠"/>
        <StatCard label={T.vacant}        value={stats.available} accent="#38bdf8" icon="🔑"/>
        <StatCard label={T.overdue}       value={stats.overdue}   accent="#ef4444" icon="⚠️"/>
        <StatCard label={T.dueSoon}       value={stats.dueSoon}   accent="#f59e0b" icon="📅"/>
        <StatCard label={T.expiring}      value={stats.expiring}  accent="#f97316" icon="⏳"/>
      </div>

      {/* Alerts */}
      {stats.available>0&&<AlertBar color="38bdf8" emoji="🔑" title={T.alertVacant} items={enriched.filter(t=>t.available).map(t=>`وحدة ${t.unit} · ${t.district}`)}/>}
      {(stats.overdue>0||stats.dueSoon>0)&&<AlertBar color="ef4444" emoji="⚡" title={T.alertAttention} items={enriched.filter(t=>["overdue","due_today"].includes(t.status)).map(t=>`وحدة ${t.unit} · ${t.name} · ${fmtDate(t.currentSlot?.dueDate)}`)}/>}
      {stats.expiring>0&&<AlertBar color="f97316" emoji="📋" title={T.alertExpiring} items={enriched.filter(t=>!t.available&&t.daysToExpiry!==null&&t.daysToExpiry>=0&&t.daysToExpiry<=7).map(t=>`وحدة ${t.unit} · ${t.name} · ${t.daysToExpiry===0?"اليوم":`${t.daysToExpiry} يوم`}`)}/>}

      {/* Toolbar */}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={T.searchPh}
          style={{padding:"7px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,0.08)",background:"rgba(255,255,255,0.04)",color:"#e2e8f0",fontSize:13,outline:"none",minWidth:210,textAlign:"right",direction:"rtl",fontFamily:"'Tajawal',sans-serif"}}/>
        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
          {fBtn("all",T.fAll)} {fBtn("available",T.fVacant)} {fBtn("overdue",T.fOverdue)} {fBtn("due_soon",T.fDueSoon)} {fBtn("paid",T.fPaid)} {fBtn("expired",T.fExpired)}
        </div>
        <select value={distFilter} onChange={e=>setDistFilter(e.target.value)}
          style={{padding:"7px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,0.08)",background:"#181b27",color:"#94a3b8",fontSize:13,outline:"none",direction:"rtl",fontFamily:"'Tajawal',sans-serif"}}>
          <option value="all">{T.allDistricts}</option>
          {DISTRICTS.map(d=><option key={d} value={d}>{d}</option>)}
        </select>
        <div style={{marginRight:"auto",display:"flex",gap:8}}>
          <button onClick={()=>setShowExport(true)} style={{padding:"7px 14px",borderRadius:8,background:"rgba(200,160,60,0.1)",border:"1px solid rgba(200,160,60,0.22)",color:"#c8a03c",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>{T.exportBtn}</button>
          {/* "Add tenant" button removed — tenants are onboarded via the Invoices/Registration tab. Dashboard is read-only status. */}
        </div>
      </div>

      {/* Mobile: stacked cards — each unit gets full info + reachable buttons (no horizontal scroll) */}
      {isMobile && (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {filtered.length===0 && <div style={{textAlign:"center",padding:40,color:"#334155",background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:16}}>{S.noResults}</div>}
          {filtered.map(t=>{
            const slot=t.currentSlot; const dl=t.daysToExpiry; const isA=t.available; const isE=t.status==="expired";
            return(
              <div key={t.id} style={{background:isA?"rgba(56,189,248,0.05)":"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:14,opacity:isE?0.55:1}}>
                {/* Top row: unit number + district + status badge */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,gap:8}}>
                  <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontSize:22,fontWeight:800,color:"#c8a03c"}}>{t.unit}</span>
                    <span style={{fontSize:13,color:"#94a3b8"}}>· {t.district}</span>
                  </div>
                  <Badge status={t.status}/>
                </div>
                {/* Body */}
                {isA ? (
                  <div style={{fontSize:13,color:"#38bdf8",fontStyle:"italic",marginBottom:12}}>{T.vacantTag}</div>
                ) : (
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12,marginBottom:12}}>
                    <div><span style={{color:"#475569"}}>{T.colTenant}: </span><span style={{color:"#e2e8f0",fontWeight:600}}>{t.name||"—"}</span></div>
                    <div style={{direction:"ltr",textAlign:"right"}}><span style={{color:"#475569"}}>{T.colPhone}: </span><span style={{color:"#94a3b8"}}>{t.phone||"—"}</span></div>
                    <div style={{gridColumn:"1 / -1"}}><span style={{color:"#475569"}}>{T.colContract}: </span><span style={{color:"#94a3b8"}}>{fmtDate(t.contractStart)} ← {fmtDate(t.contractEnd)}</span></div>
                    <div><span style={{color:"#475569"}}>{T.colRent}: </span><span style={{color:"#e2e8f0",fontWeight:700}}>{t.rent>0?t.rent.toLocaleString():"—"} ريال</span></div>
                    <div><span style={{color:"#475569"}}>{T.colDue}: </span><span style={{color:t.status==="overdue"?"#ef4444":"#94a3b8"}}>{slot?fmtDate(slot.dueDate):"—"}</span></div>
                    {dl!==null && <div style={{gridColumn:"1 / -1"}}><span style={{color:"#475569"}}>{T.colDaysLeft}: </span><span style={{color:dl<0?"#6b7280":dl===0?"#f59e0b":dl<=7?"#f97316":"#22c55e",fontWeight:700}}>{dl<0?`منذ ${Math.abs(dl)} يوم`:dl===0?T.today:`${dl} يوم`}</span></div>}
                  </div>
                )}
                {/* Read-only dashboard — view-only. Payment status is reflected from the Invoices tab. Renting and editing happen there too. */}
                {!isA && (
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={()=>setShowDetail(t.id)} style={{flex:1,minWidth:90,padding:"10px 12px",borderRadius:10,background:"rgba(200,160,60,0.14)",border:"1px solid rgba(200,160,60,0.32)",color:"#c8a03c",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>{T.viewBtn}</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop: original table */}
      {!isMobile && (
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:16,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr style={{background:"rgba(0,0,0,0.3)"}}>
            {[T.colUnit,T.colDistrict,T.colTenant,T.colPhone,T.colContract,T.colRent,T.colDue,T.colDaysLeft,T.colStatus,T.colActions].map(h=>(
              <th key={h} style={{padding:"11px 14px",textAlign:"right",fontSize:11,color:"#475569",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {filtered.length===0&&<tr><td colSpan={10} style={{textAlign:"center",padding:40,color:"#334155"}}>{S.noResults}</td></tr>}
            {filtered.map(t=>{
              const slot=t.currentSlot; const dl=t.daysToExpiry; const isA=t.available; const isE=t.status==="expired";
              return(
                <tr key={t.id} className="trow" onClick={()=>handleRowTap(t)} style={{borderTop:"1px solid rgba(255,255,255,0.04)",opacity:isE?0.5:1,background:isA?"rgba(56,189,248,0.03)":"transparent"}}>
                  <td style={{padding:"12px 14px"}}><span style={{fontSize:20,fontWeight:800,color:"#c8a03c"}}>{t.unit}</span></td>
                  <td style={{padding:"12px 14px",fontSize:13,color:"#94a3b8"}}>{t.district}</td>
                  <td style={{padding:"12px 14px"}}>{isA?<span style={{fontSize:12,color:"#38bdf8",fontStyle:"italic"}}>{T.vacantTag}</span>:<span style={{fontSize:14,fontWeight:600,color:"#e2e8f0"}}>{t.name}</span>}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:"#64748b",direction:"ltr",textAlign:"right"}}>{t.phone||"—"}</td>
                  <td style={{padding:"12px 14px"}}>{isA?<span style={{color:"#334155"}}>—</span>:<div><div style={{fontSize:12,color:"#94a3b8"}}>{fmtDate(t.contractStart)}</div><div style={{fontSize:11,color:"#475569"}}>← {fmtDate(t.contractEnd)}</div></div>}</td>
                  <td style={{padding:"12px 14px",fontSize:14,fontWeight:700,color:t.rent>0?"#e2e8f0":"#334155"}}>{t.rent>0?t.rent.toLocaleString():"—"}</td>
                  <td style={{padding:"12px 14px",fontSize:12,color:t.status==="overdue"?"#ef4444":"#94a3b8",whiteSpace:"nowrap"}}>{slot?fmtDate(slot.dueDate):"—"}</td>
                  <td style={{padding:"12px 14px"}}>{isA?<span style={{color:"#38bdf8",fontWeight:700,fontSize:13}}>{T.available}</span>:dl===null?<span style={{color:"#334155"}}>—</span>:<span style={{fontSize:13,fontWeight:700,color:dl<0?"#6b7280":dl===0?"#f59e0b":dl<=7?"#f97316":"#22c55e"}}>{dl<0?(lang==="en"?`${Math.abs(dl)}d ago`:`منذ ${Math.abs(dl)} يوم`):dl===0?T.today:`${dl} ${lang==="en"?"days":"يوم"}`}</span>}</td>
                  <td style={{padding:"12px 14px"}}><Badge status={t.status}/></td>
                  <td style={{padding:"12px 14px"}}>
                    <div style={{display:"flex",gap:5,justifyContent:"flex-end"}}>
                      {/* Read-only dashboard — view-only. Payments/rentals/edits flow through Invoices and Contracts tabs. */}
                      {!isA&&<button className="act-btn" onClick={(e)=>{e.stopPropagation();setShowDetail(t.id);}} style={actBtnStyle("#c8a03c")}>{T.viewBtn}</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      <div style={{marginTop:8,fontSize:12,color:"#334155",textAlign:"left"}}>{filtered.length} مستأجر</div>

      {/* Modals */}
      {showAdd&&(
        <Modal title={addForm._targetId?`تأجير وحدة ${addForm.unit}`:".إضافة مستأجر جديد"} onClose={()=>{setShowAdd(false);setAddForm({});}}>
          {!addForm._targetId&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}><Field label={T.unitNo} value={addForm.unit||""} onChange={v=>setAddForm(f=>({...f,unit:v}))} required/><Field label={T.districtField} value={addForm.district||""} onChange={v=>setAddForm(f=>({...f,district:v}))} required/></div>}
          <Field label={T.tenantName} value={addForm.name||""} onChange={v=>setAddForm(f=>({...f,name:v}))} required/>
          <Field label={T.phoneId} value={addForm.phone||""} onChange={v=>setAddForm(f=>({...f,phone:v}))}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label={T.contractStart} value={addForm.contractStart||""} onChange={v=>setAddForm(f=>({...f,contractStart:v}))} type="date" required/>
            <Field label={T.contractEnd} value={addForm.contractEnd||""} onChange={v=>setAddForm(f=>({...f,contractEnd:v}))} type="date" required/>
          </div>
          <Field label={T.monthlyRent} value={addForm.rent||""} onChange={v=>setAddForm(f=>({...f,rent:v}))} type="number"/>
          <SaveBtn onClick={saveTenant} disabled={!addForm.name||!addForm.contractStart||!addForm.contractEnd||(addForm._targetId?false:!addForm.unit)}/>
        </Modal>
      )}

      {showEdit&&editForm.id&&(
        <Modal title={lang==="en"?`Edit — Unit ${editForm.unit}`:`تعديل — وحدة ${editForm.unit}`} onClose={()=>{setShowEdit(null);setEditForm({});}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}><Field label="رقم الوحدة" value={editForm.unit} onChange={v=>setEditForm(f=>({...f,unit:v}))} required/><Field label="الحي" value={editForm.district} onChange={v=>setEditForm(f=>({...f,district:v}))} required/></div>
          <Field label="اسم المستأجر" value={editForm.name} onChange={v=>setEditForm(f=>({...f,name:v}))}/>
          <Field label="رقم الجوال / الهوية" value={editForm.phone} onChange={v=>setEditForm(f=>({...f,phone:v}))}/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="بداية العقد" value={editForm.contractStart} onChange={v=>setEditForm(f=>({...f,contractStart:v}))} type="date"/>
            <Field label="نهاية العقد" value={editForm.contractEnd} onChange={v=>setEditForm(f=>({...f,contractEnd:v}))} type="date"/>
          </div>
          <Field label="الإيجار الشهري (ريال)" value={editForm.rent} onChange={v=>setEditForm(f=>({...f,rent:v}))} type="number"/>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:"10px 14px",marginBottom:14}}>
            <span style={{fontSize:13,color:"#94a3b8"}}>{T.markVacant}</span>
            <button onClick={()=>markAvail(editForm.id)} style={{padding:"5px 14px",borderRadius:8,background:"rgba(239,68,68,0.12)",border:"1px solid rgba(239,68,68,0.25)",color:"#ef4444",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Tajawal',sans-serif"}}>{T.vacateBtn}</button>
          </div>
          <SaveBtn onClick={saveEdit} disabled={!editForm.unit||!editForm.district}/>
        </Modal>
      )}

      {showDetail&&detailT&&(
        <Modal title={`وحدة ${detailT.unit} · ${detailT.name}`} onClose={()=>setShowDetail(null)}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18}}>
            {[["الحي",detailT.district],["الجوال",detailT.phone||"—"],["بداية العقد",fmtDate(detailT.contractStart)],["نهاية العقد",fmtDate(detailT.contractEnd)],["الإيجار الشهري",`${detailT.rent.toLocaleString()} ريال`],["الأيام المتبقية",detailT.daysToExpiry===null?"—":detailT.daysToExpiry<0?"منتهي":`${detailT.daysToExpiry} يوم`]].map(([k,v])=>(
              <div key={k} style={{background:"rgba(255,255,255,0.03)",padding:"10px 14px",borderRadius:8,border:"1px solid rgba(255,255,255,0.06)"}}>
                <div style={{fontSize:11,color:"#475569",marginBottom:3}}>{k}</div>
                <div style={{fontSize:14,color:"#e2e8f0",fontWeight:600}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{fontSize:12,color:"#475569",marginBottom:10,fontWeight:700}}>{T.payHistory}</div>
          {detailT.slots.map(slot=>{
            const p=detailT.payments[slot.key]; const due=parseDate(slot.dueDate); const ovr=!p?.paid&&due<TODAY;
            return(
              <div key={slot.key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 14px",borderRadius:8,marginBottom:6,background:p?.paid?"rgba(34,197,94,0.07)":ovr?"rgba(239,68,68,0.07)":"rgba(255,255,255,0.03)",border:"1px solid",borderColor:p?.paid?"rgba(34,197,94,0.15)":ovr?"rgba(239,68,68,0.15)":"rgba(255,255,255,0.06)"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"#e2e8f0"}}>استحقاق: {fmtDate(slot.dueDate)}</div>
                  {p?.paid&&<div style={{fontSize:11,color:"#22c55e"}}>دُفع بتاريخ {fmtDate(p.date)}</div>}
                  {!p?.paid&&ovr&&<div style={{fontSize:11,color:"#ef4444"}}>متأخر</div>}
                  {!p?.paid&&!ovr&&<div style={{fontSize:11,color:"#64748b"}}>معلق</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13,fontWeight:700,color:"#c8a03c"}}>{detailT.rent.toLocaleString()} ريال</span>
                  {p?.paid ? <span style={{fontSize:16}}>✅</span> : <span style={{fontSize:11,color:"#64748b"}}>—</span>}
                </div>
              </div>
            );
          })}
        </Modal>
      )}

      {showExport&&(
        <Modal title={T.reportTitle} onClose={()=>setShowExport(false)}>
          <pre style={{background:"rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.07)",borderRadius:10,padding:16,fontSize:12,color:"#94a3b8",lineHeight:1.8,maxHeight:360,overflowY:"auto",direction:"rtl",textAlign:"right",whiteSpace:"pre-wrap",fontFamily:"'Tajawal',sans-serif"}}>{exportReport()}</pre>
          <button onClick={()=>navigator.clipboard.writeText(exportReport())} style={{width:"100%",padding:11,borderRadius:10,background:"linear-gradient(135deg,#c8a03c,#9a6f10)",border:"none",color:"#0d0f1a",fontWeight:800,fontSize:14,cursor:"pointer",marginTop:14,fontFamily:"'Tajawal',sans-serif"}}>{T.copyReport}</button>
        </Modal>
      )}
    </div>
  );
}
